import React, { useMemo, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fromJS } from "immutable";
import {
  Building2,
  Briefcase,
  MapPin,
  NotepadText,
  MessagesSquare,
  Mails,
  AtSign,
  DollarSign,
  CheckCircle2,
  User,
  Phone,
  Star,
} from "lucide-react";
import { saveFavoriteTree } from "../../api/ProjectApi";
import ProjectAction from "../../actions/ProjectAction";

/* ── Helpers ── */

/** Safely extract an integer field from Immutable task data */
const getIntField = (task: any, field: string): number =>
  parseInt(task.getIn(["data", field, "value"]) || "0", 10) || 0;

/** Check if an item exists in the favorite tree */
const isFavoriteItem = (tree: any[], itemId: string): boolean => {
  for (const node of tree) {
    if (node.nodeType === "ItemLink" && node.itemId === itemId) return true;
    if (node.children?.length && isFavoriteItem(node.children, itemId)) return true;
  }
  return false;
};

/* ── Sub-components ── */

/** Footer stat badge with custom tooltip */
const StatItem: React.FC<{
  icon: React.ReactNode;
  count: number | string;
  onClick?: (e: React.MouseEvent) => void;
  tooltip?: string;
}> = ({ icon, count, onClick, tooltip }) => {
  const hasValue = count && count !== 0 && count !== "0";
  return (
    <div
      onClick={onClick}
      className={`relative group/stat flex items-center gap-1 text-xs text-gray-400 ${onClick ? "cursor-pointer hover:text-gray-600 transition-colors" : ""}`}
    >
      <span className="shrink-0">{icon}</span>
      {hasValue && <span>{count}</span>}
      {tooltip && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 text-[11px] text-white bg-gray-800 rounded whitespace-nowrap opacity-0 invisible group-hover/stat:opacity-100 group-hover/stat:visible transition-all duration-150 pointer-events-none z-50">
          {tooltip}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
        </div>
      )}
    </div>
  );
};

/* ── Main Component ── */

/** Hover info dialog that shows when hovering over the card name */
const NameHoverCard: React.FC<{
  task: any;
  taskType: string;
  onClickName?: (tab?: string) => void;
}> = ({ task, taskType, onClickName }) => {
  const dispatch = useDispatch();
  const taskId = task.get("id") || "";
  const projectId = task.get("projectId") || "";
  const favoriteTree =
    useSelector((state: any) => state.projectReducer.getIn(["project", "favoriteTree"])) ||
    fromJS([]);
  const isFav = useMemo(() => isFavoriteItem(favoriteTree.toJS(), taskId), [favoriteTree, taskId]);
  const [toggling, setToggling] = useState(false);

  const handleToggleFavorite = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (toggling) return;
      setToggling(true);
      try {
        const tree = favoriteTree.toJS();
        let updatedTree;
        if (isFav) {
          const removeById = (nodes: any[]): any[] =>
            nodes
              .filter((n: any) => !(n.nodeType === "ItemLink" && n.itemId === taskId))
              .map((n: any) => ({
                ...n,
                children: n.children?.length ? removeById(n.children) : [],
              }));
          updatedTree = removeById(tree);
        } else {
          const name = task.getIn(["data", "name", "value"]) || task.get("title") || "";
          updatedTree = [
            ...tree,
            {
              id: `ItemLink-${taskId}`,
              name,
              nodeType: "ItemLink",
              itemId: taskId,
              itemType: taskType,
              iconUrl,
            },
          ];
        }
        await saveFavoriteTree(projectId, updatedTree);
        dispatch(ProjectAction.updateFavoriteTree(updatedTree));
      } finally {
        setToggling(false);
      }
    },
    [toggling, favoriteTree, isFav, taskId, taskType, projectId, task, dispatch]
  );

  const name = task.getIn(["data", "name", "value"]) || task.get("title") || "";
  const iconUrl = task.get("iconUrl") || "";

  // Extract fields based on task type
  const website = task.getIn(["data", "website", "value"]) || "";
  const domains = task.getIn(["data", "domains", "value"]);
  const email = task.getIn(["data", "email", "value"]) || "";
  const phone = task.getIn(["data", "phone", "value"]) || "";
  const industry = task.getIn(["data", "industry", "value"]) || "";
  const account = task.getIn(["data", "account", "value", "label"]) || "";
  const jobTitle = task.getIn(["data", "jobTitle", "value"]) || "";
  const stage = task.getIn(["data", "stage", "value"]) || "";
  const estimatedRevenue = task.getIn(["data", "estimatedRevenue", "value"]) || "";

  const country = (() => {
    const label = task.getIn(["data", "country", "value", "label"]);
    if (label) return label;
    const val = task.getIn(["data", "country", "value"]);
    if (val && typeof val === "string") return val;
    return "";
  })();

  // Activity counts
  const totalNotes = getIntField(task, "numberOfNotes");
  const totalActivities = getIntField(task, "numberOfTasks");
  const totalComments = getIntField(task, "numberOfComments");
  const totalEmails = getIntField(task, "numberOfEmails");

  // Resolve domain/subtitle display
  const subtitle = useMemo(() => {
    if (website) {
      try {
        return website
          .replace(/^https?:\/\//, "")
          .replace(/^www\./, "")
          .replace(/\/$/, "");
      } catch {
        return website;
      }
    }
    if (domains) {
      const list = domains.toJS
        ? domains.toJS()
        : typeof domains === "string"
          ? domains.split(",").filter(Boolean)
          : [];
      if (list.length > 0) return list[0];
    }
    if (taskType === "CONTACT" && account) return account;
    if (taskType === "SALE" && stage) return stage;
    return "";
  }, [website, domains, taskType, account, stage]);

  // Communication status
  const hasCommunication = totalEmails > 0 || totalComments > 0;

  // Build detail rows based on taskType
  const detailRows: { icon: React.ReactNode; text: string }[] = [];

  if (taskType === "COMPANY") {
    if (industry)
      detailRows.push({
        icon: <Briefcase className="w-3.5 h-3.5 text-gray-400" />,
        text: industry,
      });
    if (country)
      detailRows.push({ icon: <MapPin className="w-3.5 h-3.5 text-gray-400" />, text: country });
    if (email)
      detailRows.push({ icon: <AtSign className="w-3.5 h-3.5 text-gray-400" />, text: email });
    if (phone)
      detailRows.push({ icon: <Phone className="w-3.5 h-3.5 text-gray-400" />, text: phone });
  } else if (taskType === "CONTACT") {
    if (jobTitle)
      detailRows.push({
        icon: <Briefcase className="w-3.5 h-3.5 text-gray-400" />,
        text: jobTitle,
      });
    if (email)
      detailRows.push({ icon: <AtSign className="w-3.5 h-3.5 text-gray-400" />, text: email });
    if (phone)
      detailRows.push({ icon: <Phone className="w-3.5 h-3.5 text-gray-400" />, text: phone });
    if (country)
      detailRows.push({ icon: <MapPin className="w-3.5 h-3.5 text-gray-400" />, text: country });
  } else if (taskType === "SALE") {
    if (estimatedRevenue) {
      const num = parseFloat(estimatedRevenue);
      detailRows.push({
        icon: <DollarSign className="w-3.5 h-3.5 text-gray-400" />,
        text: isNaN(num) ? estimatedRevenue : `$${num.toLocaleString()}`,
      });
    }
    if (stage)
      detailRows.push({
        icon: <CheckCircle2 className="w-3.5 h-3.5 text-gray-400" />,
        text: stage,
      });
  }

  return (
    <div className="min-w-[360px] bg-white rounded-lg shadow-lg border border-gray-200 p-3 space-y-2.5">
      {/* Header: icon + name + subtitle */}
      <div className="flex items-center gap-2.5">
        <span className="shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-500 overflow-hidden">
          {iconUrl ? (
            <img
              src={iconUrl}
              alt=""
              className="w-8 h-8 object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          ) : taskType === "CONTACT" ? (
            <User className="w-4 h-4" />
          ) : taskType === "SALE" ? (
            <DollarSign className="w-4 h-4" />
          ) : (
            <Building2 className="w-4 h-4" />
          )}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span
              onClick={(e) => {
                e.stopPropagation();
                onClickName?.();
              }}
              className="text-sm font-semibold text-gray-900 truncate underline decoration-gray-300 hover:decoration-gray-900 cursor-pointer transition-colors"
            >
              {name}
            </span>
            <button
              type="button"
              onClick={handleToggleFavorite}
              disabled={toggling}
              className="relative group/fav shrink-0 p-0.5 rounded hover:bg-gray-100 transition-colors disabled:opacity-50"
              title={isFav ? "Remove from favorites" : "Add to favorites"}
            >
              <Star
                className={`w-4 h-4 transition-colors ${
                  isFav ? "fill-amber-400 text-amber-400" : "text-gray-300 hover:text-amber-400"
                }`}
              />
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 px-2 py-1 text-[11px] text-white bg-gray-800 rounded whitespace-nowrap opacity-0 invisible group-hover/fav:opacity-100 group-hover/fav:visible transition-all duration-150 pointer-events-none z-50">
                {isFav ? "Remove from favorites" : "Add to favorites"}
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-800" />
              </div>
            </button>
          </div>
          <span className="text-xs text-gray-500 truncate block">{subtitle || "No domain"}</span>
        </div>
      </div>

      {/* Detail rows */}
      {detailRows.length > 0 && (
        <div className="space-y-1.5">
          {detailRows.map((row, idx) => (
            <div key={idx} className="flex items-center gap-2 text-xs text-gray-600">
              {row.icon}
              <span className="truncate">{row.text}</span>
            </div>
          ))}
        </div>
      )}

      {/* Communication status */}
      <div className="flex items-center gap-1.5 text-xs text-gray-500">
        <span
          className={`w-2 h-2 rounded-full shrink-0 ${hasCommunication ? "bg-green-400" : "bg-gray-300"}`}
        />
        <span>{hasCommunication ? "Communication active" : "No communication found"}</span>
      </div>

      {/* Quick stats */}
      <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
        <StatItem
          icon={<NotepadText className="w-3.5 h-3.5" />}
          count={totalNotes}
          tooltip="Notes"
          onClick={(e) => {
            e.stopPropagation();
            onClickName?.("commented");
          }}
        />
        <StatItem
          icon={<CheckCircle2 className="w-3.5 h-3.5" />}
          count={totalActivities}
          tooltip="Tasks"
          onClick={(e) => {
            e.stopPropagation();
            onClickName?.("task");
          }}
        />
        <StatItem
          icon={<MessagesSquare className="w-3.5 h-3.5" />}
          count={totalComments}
          tooltip="Comments"
          onClick={(e) => {
            e.stopPropagation();
            onClickName?.("task");
          }}
        />
        <StatItem
          icon={<Mails className="w-3.5 h-3.5" />}
          count={totalEmails}
          tooltip="Emails"
          onClick={(e) => {
            e.stopPropagation();
            onClickName?.("sendEmail");
          }}
        />
      </div>
    </div>
  );
};

export default NameHoverCard;
